import { type DebugConfig, DebugLogger, debug } from './debug';

export interface VGroveClientConfig {
    /** 调试配置 */
    debug?: DebugConfig;
    /** 是否启用开发者工具 */
    devtools?: boolean;
    /** 是否允许重复初始化（开发环境下默认为true） */
    allowReinitialization?: boolean;
    /** 其他配置项（预留） */
    [key: string]: unknown;
}

interface VGroveClientState {
    initialized: boolean;
    config: VGroveClientConfig;
}

class VGroveClientManager {
    private static instance: VGroveClientManager;
    private state: VGroveClientState = {
        initialized: false,
        config: {}
    };

    private constructor() { }

    static getInstance(): VGroveClientManager {
        if (!VGroveClientManager.instance) {
            VGroveClientManager.instance = new VGroveClientManager();
        }
        return VGroveClientManager.instance;
    }

    /**
     * 初始化 VGrove Client
     * @param config 配置选项
     */
    init(config: VGroveClientConfig = {}): void {
        // 合并默认配置
        const defaultConfig: VGroveClientConfig = {
            debug: {
                enabled: false, // 默认关闭调试，用户需要显式启用
                auth: false,
                i18n: false,
                performance: false, // 性能日志也默认关闭
                routing: false,
                storage: false
            },
            devtools: this.isDevelopment(), // 开发者工具只在开发环境中启用
            allowReinitialization: this.isDevelopment() // 开发环境下默认允许重复初始化
        };

        const newConfig = this.mergeConfig(defaultConfig, config);

        // 如果已经初始化过了
        if (this.state.initialized) {
            // 检查是否允许重复初始化
            const allowReinitialization = newConfig.allowReinitialization ?? this.isDevelopment();

            if (!allowReinitialization) {
                debug.warn('VGrove Client已经初始化过了，且不允许重复初始化');
                return;
            }

            // 比较配置是否相同
            if (this.isConfigEqual(this.state.config, newConfig)) {
                // 配置相同，静默跳过
                return;
            }

            // 配置不同，在开发环境下允许重新初始化
            if (this.isDevelopment()) {
                debug.info('VGrove Client 配置已更新，重新初始化', {
                    oldConfig: this.state.config,
                    newConfig: newConfig
                });
            } else {
                debug.warn('VGrove Client已经初始化过了，但配置不同。在生产环境下不建议重复初始化');
                return;
            }
        }

        this.state.config = newConfig;
        this.state.initialized = true;

        // 初始化调试系统
        if (this.state.config.debug) {
            DebugLogger.updateConfig(this.state.config.debug);
        }

        // 开发环境下暴露调试工具
        if (this.state.config.devtools && this.isDevelopment()) {
            this.exposeDevtools();
        }

        debug.success('VGrove Client 初始化完成', {
            environment: this.isDevelopment() ? 'development' : 'production',
            debug: this.state.config.debug,
            devtools: this.state.config.devtools,
            allowReinitialization: this.state.config.allowReinitialization
        });
    }

    /**
     * 更新配置
     * @param config 新的配置
     */
    updateConfig(config: Partial<VGroveClientConfig>): void {
        if (!this.state.initialized) {
            debug.warn('VGrove Client尚未初始化，请先调用 initVGroveClient()');
            return;
        }

        this.state.config = this.mergeConfig(this.state.config, config);

        // 更新调试配置
        if (config.debug) {
            DebugLogger.updateConfig(config.debug);
        }
    }

    /**
     * 获取当前配置
     */
    getConfig(): VGroveClientConfig {
        return { ...this.state.config };
    }

    /**
     * 获取初始化状态
     */
    isInitialized(): boolean {
        return this.state.initialized;
    }

    /**
     * 重置配置（主要用于测试）
     */
    reset(): void {
        this.state = {
            initialized: false,
            config: {}
        };
    }

    private isDevelopment(): boolean {
        return process.env.NODE_ENV === 'development';
    }

    /**
     * 深度比较两个配置对象是否相同
     */
    private isConfigEqual(config1: VGroveClientConfig, config2: VGroveClientConfig): boolean {
        // 简单的JSON字符串比较，对于配置对象来说足够了
        try {
            return JSON.stringify(this.sortObjectKeys(config1)) === JSON.stringify(this.sortObjectKeys(config2));
        } catch {
            // 如果比较失败，返回false以允许重新初始化
            return false;
        }
    }

    /**
     * 递归排序对象的键，确保比较的一致性
     */
    private sortObjectKeys(obj: unknown): unknown {
        if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
            return obj;
        }

        const sorted: Record<string, unknown> = {};
        Object.keys(obj).sort().forEach(key => {
            sorted[key] = this.sortObjectKeys((obj as Record<string, unknown>)[key]);
        });
        return sorted;
    }

    private mergeConfig(target: VGroveClientConfig, source: VGroveClientConfig): VGroveClientConfig {
        const result = { ...target };

        for (const key in source) {
            if (source[key] !== undefined) {
                if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
                    result[key] = this.mergeConfig((result[key] as VGroveClientConfig) || ({} as VGroveClientConfig), source[key] as VGroveClientConfig);
                } else {
                    result[key] = source[key];
                }
            }
        }

        return result;
    }

    private exposeDevtools(): void {
        if (typeof window !== 'undefined') {
            (window as unknown as Record<string, unknown>).__VGROVE_DEVTOOLS__ = {
                // 调试控制
                debug: {
                    enable: () => this.updateConfig({ debug: { enabled: true } }),
                    disable: () => this.updateConfig({ debug: { enabled: false } }),
                    enableModule: (module: keyof DebugConfig) => {
                        if (module !== 'enabled') {
                            this.updateConfig({ debug: { [module]: true } });
                        }
                    },
                    disableModule: (module: keyof DebugConfig) => {
                        if (module !== 'enabled') {
                            this.updateConfig({ debug: { [module]: false } });
                        }
                    },
                    getConfig: () => DebugLogger.getInstance().getConfig(),
                    status: () => {
                        const config = DebugLogger.getInstance().getConfig();
                        if (DebugLogger.getInstance().getConfig().enabled) {
                            debug.routing('调试配置状态', config);
                        }
                        return config;
                    }
                },
                // 客户端状态
                client: {
                    getConfig: () => this.getConfig(),
                    getState: () => ({
                        initialized: this.isInitialized(),
                        config: this.getConfig()
                    })
                }
            };

            // 只在调试模式下显示开发者工具说明
            if (this.state.config.debug?.enabled) {
                debug.success('开发者工具已暴露到 window.__VGROVE_DEVTOOLS__');
                debug.routing('使用方式', {
                    'status()': '查看调试状态',
                    'enable()': '启用调试',
                    'disable()': '禁用调试',
                    'enableModule("auth")': '启用特定模块',
                    'client.getState()': '查看客户端状态'
                });
            }
        }
    }
}

// 导出便捷的初始化函数
export const initVGroveClient = (config?: VGroveClientConfig): void => {
    VGroveClientManager.getInstance().init(config);
};

// 导出配置更新函数
export const updateVGroveClientConfig = (config: Partial<VGroveClientConfig>): void => {
    VGroveClientManager.getInstance().updateConfig(config);
};

// 导出状态获取函数
export const getVGroveClientConfig = (): VGroveClientConfig => {
    return VGroveClientManager.getInstance().getConfig();
};

// 导出初始化状态检查函数
export const isVGroveClientInitialized = (): boolean => {
    return VGroveClientManager.getInstance().isInitialized();
};

// 导出重置函数（主要用于测试）
export const resetVGroveClient = (): void => {
    VGroveClientManager.getInstance().reset();
};

/**
 * 强制重新初始化 VGrove Client
 * 这会先重置然后重新初始化，适用于需要完全重新设置的场景
 * @param config 新的配置选项
 */
export const forceReinitVGroveClient = (config?: VGroveClientConfig): void => {
    const manager = VGroveClientManager.getInstance();
    manager.reset();
    manager.init(config);
};

// 默认配置
export const defaultVGroveClientConfig: VGroveClientConfig = {
    debug: {
        enabled: false, // 默认关闭调试，用户需要显式启用
        auth: false,
        i18n: false,
        performance: false,
        routing: false,
        storage: false
    },
    devtools: process.env.NODE_ENV === 'development', // 开发者工具只在开发环境中启用
    allowReinitialization: process.env.NODE_ENV === 'development' // 开发环境下默认允许重复初始化
};